import type { ComponentType, SVGProps } from 'react';
import { cn } from '@/shared/utils/cn';
import type { DomainIconId } from './domainIconTypes';
import { BufaloGlyph, CerdoGlyph, VacaGlyph } from './glyphs/ganado';
import { FrutaGlyph, HortalizaGlyph, LecheGlyph, QuesoGlyph } from './glyphs/campo';
import {
  BalanzaGlyph,
  BilleteGlyph,
  CamionGlyph,
  CampanaGlyph,
  MercadoGlyph,
  PizarraGlyph,
} from './glyphs/mercado';

const GLYPHS: Record<DomainIconId, ComponentType> = {
  vaca: VacaGlyph,
  bufalo: BufaloGlyph,
  cerdo: CerdoGlyph,
  leche: LecheGlyph,
  queso: QuesoGlyph,
  hortaliza: HortalizaGlyph,
  fruta: FrutaGlyph,
  billete: BilleteGlyph,
  pizarra: PizarraGlyph,
  balanza: BalanzaGlyph,
  camion: CamionGlyph,
  campana: CampanaGlyph,
  mercado: MercadoGlyph,
};

export interface DomainIconProps extends Omit<SVGProps<SVGSVGElement>, 'ref' | 'children'> {
  id: DomainIconId;
  /** Lado en px. 16/20/24 en la interfaz; mayor en estados vacíos y selectores. */
  size?: number;
  /** Texto para lectores de pantalla. Sin él, el icono es decorativo (aria-hidden). */
  label?: string;
}

/** Icono de dominio propio: mismo grid (24), mismo trazo (1,75) y mismos remates que el set de interfaz. */
export function DomainIcon({ id, size = 20, label, className, ...rest }: DomainIconProps) {
  const Glyph = GLYPHS[id];
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('shrink-0', className)}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      {...rest}
    >
      <Glyph />
    </svg>
  );
}
